import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { SUBDOMAIN } from './lib/subdomains.js';
import UpdateNotification from './components/UpdateNotification.jsx';
import Footer from './components/Layout/Footer.jsx';
import SiteNavbar from './components/Layout/SiteNavbar.jsx';
import { ChatProvider, useChat } from './context/ChatContext.jsx';
import { ThemeProvider } from './context/ThemeContext.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import V7CornerCard from './components/Presence/variants/V7CornerCard.jsx';
import { PresenceProvider } from './components/Presence/PresenceContext.jsx';
import PageTransition from './components/PageTransition.jsx';
import BriefShell from './components/Layout/BriefShell.jsx';
import Home from './pages/Home.jsx';

const Admin = lazy(() => import('./pages/Admin.jsx'));
const AskClaudePanel = lazy(() => import('./components/AskClaude/AskClaudePanel.jsx'));
const BriefPage = lazy(() => import('./pages/BriefPage.jsx'));
const GetTilted = lazy(() => import('./pages/GetTilted.jsx'));
const HouseRules = lazy(() => import('./pages/HouseRules.jsx'));
const Party = lazy(() => import('./pages/Party.jsx'));
const Resume = lazy(() => import('./pages/Resume.jsx'));
const SessionDetail = lazy(() => import('./pages/SessionDetail.jsx'));
const Sessions = lazy(() => import('./pages/Sessions.jsx'));
const TeldireLanding = lazy(() => import('./pages/TeldireLanding.jsx'));
const TeldireSessionDetail = lazy(() => import('./pages/TeldireSessionDetail.jsx'));
const TeldireSessions = lazy(() => import('./pages/TeldireSessions.jsx'));
const TeldireWikiIndex = lazy(() => import('./pages/TeldireWikiIndex.jsx'));
const TeldireWikiPage = lazy(() => import('./pages/TeldireWikiPage.jsx'));
const Timeline = lazy(() => import('./pages/Timeline.jsx'));
const WexfordLanding = lazy(() => import('./pages/WexfordLanding.jsx'));
const WikiPage = lazy(() => import('./pages/WikiPage.jsx'));

const WEXFORD_PATHS = ['/wexford', '/timeline', '/wiki', '/party', '/house-rules', '/sessions', '/admin'];

function RouteFallback() {
  return (
    <div
      aria-hidden="true"
      style={{
        minHeight: 'calc(100vh - 60px)',
        background: 'transparent',
      }}
    />
  );
}

function RouteSuspense({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function LazyAskClaudePanel() {
  const { isExpanded, messages } = useChat();

  if (!isExpanded && messages.length === 0) return null;

  return (
    <Suspense fallback={null}>
      <AskClaudePanel />
    </Suspense>
  );
}

function getSurfaceBranding(pathname, subdomain) {
  if (subdomain === 'brief') {
    return {
      themeColor: '#C96442',
      manifest: '/brief-manifest.json',
      iconHref: '/icons/brief-192.png',
      iconType: 'image/png',
    };
  }

  if (subdomain === 'wexford') {
    return {
      themeColor: '#0d1117',
      manifest: '/manifest.webmanifest',
      iconHref: '/icons/wexford-app.svg',
      iconType: 'image/svg+xml',
    };
  }

  if (subdomain === 'teldire' || pathname === '/teldire' || pathname.startsWith('/teldire/')) {
    return {
      themeColor: '#DDCEBF',
      manifest: '/manifest.webmanifest',
      iconHref: '/icons/teldire-app.svg',
      iconType: 'image/svg+xml',
    };
  }

  if (subdomain === 'resume' || pathname === '/resume') {
    return {
      themeColor: '#DDCEBF',
      manifest: '/manifest.webmanifest',
      iconHref: '/icons/daulton-app.svg',
      iconType: 'image/svg+xml',
    };
  }

  if (subdomain === 'wexford' || WEXFORD_PATHS.some(p => pathname === p || pathname.startsWith(`${p}/`))) {
    return {
      themeColor: '#0d1117',
      manifest: '/manifest.webmanifest',
      iconHref: '/icons/wexford-app.svg',
      iconType: 'image/svg+xml',
    };
  }

  return {
    themeColor: '#DDCEBF',
    manifest: '/manifest.webmanifest',
    iconHref: '/icons/daulton-app.svg',
    iconType: 'image/svg+xml',
  };
}

function getPageTitle(pathname, subdomain) {
  if (subdomain === 'brief') return 'Morning Brief';
  if (subdomain === 'resume') return 'Daulton Jenkins';

  if (subdomain === 'wexford') {
    if (pathname === '/' || pathname === '/wexford') return 'Wexford Reach';
    if (pathname === '/timeline') return 'Timeline | Wexford Reach';
    if (pathname === '/party') return 'Party | Wexford Reach';
    if (pathname === '/house-rules') return 'House Rules | Wexford Reach';
    if (pathname === '/sessions' || pathname.startsWith('/sessions/')) return 'Sessions | Wexford Reach';
    if (pathname.startsWith('/wiki/')) {
      const slug = pathname.replace('/wiki/', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return `${slug} | Wexford Reach`;
    }
    if (pathname === '/admin') return 'Admin | Wexford Reach';
    return 'Wexford Reach';
  }

  if (subdomain === 'teldire') {
    if (pathname === '/') return 'Teldire';
    if (pathname === '/wiki') return 'Character Wikis | Teldire';
    if (pathname === '/sessions') return 'Sessions | Teldire';
    if (pathname.startsWith('/sessions/')) return 'Session Detail | Teldire';
    if (pathname.startsWith('/wiki/')) {
      const slug = pathname.replace('/wiki/', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return `${slug} | Teldire`;
    }
    return 'Teldire';
  }

  if (pathname === '/') return 'Daulton';
  if (pathname === '/resume') return 'Daulton Jenkins';
  if (pathname === '/wexford') return 'Wexford Reach';
  if (pathname === '/timeline') return 'Timeline | Wexford Reach';
  if (pathname === '/party') return 'Party | Wexford Reach';
  if (pathname === '/house-rules') return 'House Rules | Wexford Reach';
  if (pathname === '/sessions' || pathname.startsWith('/sessions/')) return 'Sessions | Wexford Reach';
  if (pathname === '/teldire') return 'Teldire';
  if (pathname === '/teldire/wiki') return 'Character Wikis | Teldire';
  if (pathname === '/teldire/sessions') return 'Sessions | Teldire';
  if (pathname.startsWith('/teldire/sessions/')) return 'Session Detail | Teldire';
  if (pathname.startsWith('/wiki/')) {
    const slug = pathname.replace('/wiki/', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return `${slug} | Wexford Reach`;
  }
  if (pathname.startsWith('/teldire/wiki/')) {
    const slug = pathname.replace('/teldire/wiki/', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return `${slug} | Teldire`;
  }
  if (pathname === '/admin') return 'Admin | Wexford Reach';
  return 'Daulton';
}

function TeldireRoutes() {
  return (
    <RouteSuspense>
      <Routes>
        <Route path="/" element={<TeldireLanding />} />
        <Route path="/wiki" element={<TeldireWikiIndex />} />
        <Route path="/wiki/:slug" element={<TeldireWikiPage />} />
        <Route path="/sessions" element={<TeldireSessions />} />
        <Route path="/sessions/:slug" element={<TeldireSessionDetail />} />
      </Routes>
    </RouteSuspense>
  );
}

function ResumeHostRedirect() {
  useEffect(() => {
    if (window.location.hostname === 'resume.daulton.co') {
      window.location.replace('https://daulton.co/resume');
    }
  }, []);

  return null;
}

export default function App() {
  const location = useLocation();

  const isWexford =
    SUBDOMAIN === 'wexford' ||
    (SUBDOMAIN === null && WEXFORD_PATHS.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)));

  // Don't restrict to SUBDOMAIN === null — Cloudflare preview URLs have
  // a generated subdomain (e.g. abc123.pages.dev) that isn't null.
  // The resume/wexford/teldire branches return early above, so reaching
  // here at pathname '/' always means we're on the real homepage.
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (SUBDOMAIN === 'wexford' || isWexford) {
      document.body.dataset.surface = 'wexford';
    } else {
      delete document.body.dataset.surface;
    }
  }, [isWexford]);

  useEffect(() => {
    const metadata = getSurfaceBranding(location.pathname, SUBDOMAIN);
    const themeMeta = document.querySelector('meta[name="theme-color"]');

    let manifestLink = document.querySelector('link[data-site-manifest]');
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.setAttribute('data-site-manifest', 'true');
      document.head.appendChild(manifestLink);
    }

    let iconLink = document.querySelector('link[data-site-icon]');
    if (!iconLink) {
      iconLink = document.createElement('link');
      iconLink.rel = 'icon';
      iconLink.setAttribute('data-site-icon', 'true');
      document.head.appendChild(iconLink);
    }

    document.title = getPageTitle(location.pathname, SUBDOMAIN);
    if (themeMeta) themeMeta.content = metadata.themeColor;
    manifestLink.href = metadata.manifest;
    iconLink.href = metadata.iconHref;
    iconLink.type = metadata.iconType;
  }, [location.pathname]);

  if (SUBDOMAIN === 'brief') {
    return (
      <ThemeProvider>
        <ChatProvider>
          <ScrollToTop />
          <UpdateNotification />
          <BriefShell>
            <PageTransition locationKey={location.key}>
              <RouteSuspense>
                <BriefPage />
              </RouteSuspense>
            </PageTransition>
          </BriefShell>
          <LazyAskClaudePanel />
        </ChatProvider>
      </ThemeProvider>
    );
  }

  if (SUBDOMAIN === 'resume') {
    if (window.location.hostname === 'resume.daulton.co') {
      return <ResumeHostRedirect />;
    }

    return (
      <ThemeProvider>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <ScrollToTop />
          <UpdateNotification />
          <SiteNavbar />
          <main style={{ flex: 1, paddingTop: '60px' }}>
            <PageTransition locationKey={location.key}>
              <RouteSuspense>
                <Resume />
              </RouteSuspense>
            </PageTransition>
          </main>
        </div>
      </ThemeProvider>
    );
  }

  if (SUBDOMAIN === 'wexford') {
    return (
      <ThemeProvider>
        <ChatProvider>
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <ScrollToTop />
            <UpdateNotification />
            <SiteNavbar theme="wexford" />
            <main style={{ flex: 1, paddingTop: '60px' }}>
              <PageTransition locationKey={location.key}>
                <RouteSuspense>
                  <Routes>
                    <Route path="/" element={<WexfordLanding />} />
                    <Route path="/timeline" element={<Timeline />} />
                    <Route path="/wiki/:slug" element={<WikiPage />} />
                    <Route path="/party" element={<Party />} />
                    <Route path="/house-rules" element={<HouseRules />} />
                    <Route path="/sessions" element={<Sessions />} />
                    <Route path="/sessions/:id" element={<SessionDetail />} />
                    <Route path="/admin" element={<Admin />} />
                  </Routes>
                </RouteSuspense>
              </PageTransition>
            </main>
            <Footer theme="wexford" text="Wexford Reach · Side-priority archive" />
            <LazyAskClaudePanel />
          </div>
        </ChatProvider>
      </ThemeProvider>
    );
  }

  if (SUBDOMAIN === 'teldire') {
    return (
      <ThemeProvider>
        <ChatProvider>
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <ScrollToTop />
            <UpdateNotification />
            <SiteNavbar />
            <main style={{ flex: 1, paddingTop: '60px' }}>
              <PageTransition locationKey={location.key}>
                <TeldireRoutes />
              </PageTransition>
            </main>
            <LazyAskClaudePanel />
          </div>
        </ChatProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ChatProvider>
        <PresenceProvider>
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <ScrollToTop />
            <UpdateNotification />
            <SiteNavbar theme={isWexford ? 'wexford' : 'warm'} />
            <main style={{ flex: 1, paddingTop: '60px' }}>
              <PageTransition locationKey={location.key}>
                <RouteSuspense>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/wexford" element={<WexfordLanding />} />
                    <Route path="/timeline" element={<Timeline />} />
                    <Route path="/wiki/:slug" element={<WikiPage />} />
                    <Route path="/party" element={<Party />} />
                    <Route path="/house-rules" element={<HouseRules />} />
                    <Route path="/sessions" element={<Sessions />} />
                    <Route path="/sessions/:id" element={<SessionDetail />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/resume" element={<Resume />} />
                    <Route path="/teldire" element={<TeldireLanding />} />
                    <Route path="/teldire/wiki" element={<TeldireWikiIndex />} />
                    <Route path="/teldire/wiki/:slug" element={<TeldireWikiPage />} />
                    <Route path="/teldire/sessions" element={<TeldireSessions />} />
                    <Route path="/teldire/sessions/:slug" element={<TeldireSessionDetail />} />
                    <Route path="/get-tilted" element={<GetTilted />} />
                  </Routes>
                </RouteSuspense>
              </PageTransition>
            </main>
            {isWexford && <Footer theme="wexford" text="Wexford Reach · Side-priority archive" />}
            <LazyAskClaudePanel />
            {isHomePage && <V7CornerCard />}
          </div>
        </PresenceProvider>
      </ChatProvider>
    </ThemeProvider>
  );
}
